import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useSite } from './context';
import { Busy, Failure, Field, Notice } from './bits';
import { asFailure } from '../api/errors';

/**
 * Claim-by-proof, as two screens (spec §6.4.1).
 *
 * "There is no guest username, no password, no profile that spans tenants.
 * Signing in *is* proving control of a phone or email, freshly, on this tenant's
 * site." So this component has no "create an account" affordance, no password
 * field, and no "you already have an account here" — the last one being the
 * thing §6.4.1 spends its opening paragraph forbidding, because it would tell a
 * stranger who is a client of this spa.
 *
 * ## What the UI must not add back
 *
 * The server answers **identically** whether the identity is known or unknown,
 * and this screen must not undo that. So:
 *
 *  - one message after "send", always the server's own;
 *  - the *name* field is offered on the first screen and sent with the verify,
 *    where `guest-api` uses it only when creating a brand-new client. It is
 *    never a signal: a returning guest may type a nickname and their record
 *    keeps the name the spa knows them by, which is the server's rule and shows
 *    up here as "we do not ask twice";
 *  - a wrong code, an expired code, a spent code and too many attempts are one
 *    `401` with one sentence, and it is printed as it came.
 *
 * ## Focus
 *
 * A one-time code arrives while the guest is looking at their phone's
 * notification shade, so the moment the code screen appears the caret has to be
 * in the code box — otherwise they come back to the browser and type six digits
 * into nothing. `useEffect` moves focus on the step change, and the region
 * announces itself so a screen-reader user hears the same transition.
 * `autoComplete="one-time-code"` lets iOS and Android offer the code from the
 * SMS directly.
 */

type Step = { kind: 'ask' } | { kind: 'code'; identity: string; message: string };

export function Identify({
  purpose,
  onSignedIn,
}: {
  /** What the guest is about to do — the reason the screen gives for asking. */
  purpose: string;
  onSignedIn: () => void;
}): ReactNode {
  const { api, probeSession } = useSite();
  const [step, setStep] = useState<Step>({ kind: 'ask' });
  const [identity, setIdentity] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step.kind === 'code') codeRef.current?.focus();
  }, [step.kind]);

  async function send(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const answer = await api.requestOtp(identity.trim());
      setStep({ kind: 'code', identity: identity.trim(), message: answer.message });
      setCode('');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (step.kind !== 'code') return;
    setError(null);
    setBusy(true);
    try {
      await api.verifyOtp({
        identity: step.identity,
        code: code.trim(),
        ...(name.trim() === '' ? {} : { name: name.trim() }),
      });
      // The cookie is set; the only way to see it is to ask. `probeSession` is a
      // `GET /api/me`, which is also how the confirm screen learns the name to
      // put on the booking.
      await probeSession();
      onSignedIn();
    } catch (e) {
      setError(e);
      // A failed guess burns an attempt server-side (≤ 5 per code, §6.4.4), so
      // the box is cleared rather than left holding a number that is now known
      // to be wrong.
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  if (step.kind === 'ask') {
    return (
      <form className="identify" onSubmit={(e) => void send(e)}>
        <p className="identify__why">{purpose}</p>

        <Field
          id="identity"
          label="Mobile number or email"
          hint="We text or email a six-digit code to confirm it is you. No password, no account."
        >
          <input
            id="identity"
            className="input"
            name="identity"
            type="text"
            inputMode="tel"
            autoComplete="tel"
            aria-describedby="identity-hint"
            required
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
          />
        </Field>

        <Field id="name" label="Name (optional)" hint="Only used if this is your first visit.">
          <input
            id="name"
            className="input"
            name="name"
            type="text"
            autoComplete="name"
            aria-describedby="name-hint"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        {error === null ? null : <Failure error={error} />}
        {busy ? <Busy label="Sending a code…" /> : null}

        <button className="button button--primary" type="submit" disabled={busy || identity === ''}>
          Send me a code
        </button>
      </form>
    );
  }

  return (
    <form className="identify" onSubmit={(e) => void verify(e)}>
      {/* The server's sentence, not ours — it is the same one for a known and an
          unknown identity, and rewording it here would be the first step toward
          two wordings. */}
      <Notice title={step.message}>
        <p className="notice__hint">Sent to {step.identity}.</p>
      </Notice>

      <Field id="code" label="Six-digit code">
        <input
          id="code"
          ref={codeRef}
          className="input input--code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      {error === null ? null : <Failure error={error} />}
      {busy ? <Busy label="Checking…" /> : null}

      <div className="row">
        <button
          className="button button--primary"
          type="submit"
          disabled={busy || code.length !== 6}
        >
          Confirm code
        </button>
        <button
          className="button button--quiet"
          type="button"
          onClick={() => {
            setStep({ kind: 'ask' });
            setError(null);
          }}
        >
          Use a different number
        </button>
      </div>

      {/* Deliberately not a "resend" button on a timer. The per-identity cooldown
          is server-side and answers `429`; a button that looks available and is
          not would teach a guest to hammer the one endpoint §6.4.4 calls a
          machine for converting HTTP into paid SMS. Going back and sending
          again is one tap, and the server's `429` explains itself. */}
    </form>
  );
}

/** Whether a thrown thing is the session ending, for callers that must re-ask. */
export function isSessionEnd(e: unknown): boolean {
  return asFailure(e).code === 'unauthenticated';
}
